/**
 * Network Events and Message Types
 *
 * Shared between client and server for type-safe communication
 */
// ============================================================================
// CLIENT → SERVER EVENTS
// ============================================================================
export var ClientEvent;
(function (ClientEvent) {
    // Authentication
    ClientEvent["AUTH_LOGIN"] = "auth:login";
    ClientEvent["AUTH_GUEST"] = "auth:guest";
    ClientEvent["AUTH_SOCIAL"] = "auth:social";
    ClientEvent["AUTH_LOGOUT"] = "auth:logout";
    ClientEvent["USERNAME_CHECK"] = "username:check";
    // Matchmaking
    ClientEvent["FIND_GAME"] = "match:find";
    ClientEvent["CANCEL_FIND"] = "match:cancel";
    ClientEvent["CREATE_ROOM"] = "room:create";
    ClientEvent["JOIN_ROOM"] = "room:join";
    ClientEvent["LEAVE_ROOM"] = "room:leave";
    // Game Actions
    ClientEvent["GAME_READY"] = "game:ready";
    ClientEvent["GAME_UNREADY"] = "game:unready";
    ClientEvent["GAME_BET"] = "game:bet";
    ClientEvent["GAME_REBUY"] = "game:rebuy";
    ClientEvent["GAME_BET_ZONE_CREATE"] = "game:bet_zone_create";
    ClientEvent["GAME_BET_ZONE_FADE"] = "game:bet_zone_fade";
    ClientEvent["GAME_ROLL"] = "game:roll";
    ClientEvent["GAME_ROLL_RESULT"] = "game:roll_result";
    ClientEvent["GAME_SIDE_BET"] = "game:side_bet";
    ClientEvent["GAME_REQUEST_SIDEBET_WINDOW"] = "game:request_sidebet_window";
    ClientEvent["BETTING_ACTIVITY"] = "betting:activity";
    ClientEvent["PLAYER_READY_ACK"] = "player:ready";
    ClientEvent["GAME_FORFEIT"] = "game:forfeit";
    // Craps: shooter / faders toggle pre-arm for the sidebet window
    ClientEvent["SIDEBET_BUTTON_PRESS"] = "sidebet_button:press";
    // Server-Authoritative Dice Phase Events
    ClientEvent["DICE_PICKUP_START"] = "dice:pickup_start";
    ClientEvent["DICE_COLLECTED"] = "dice:collected";
    ClientEvent["DICE_THROW"] = "dice:throw";
    ClientEvent["DICE_STOPPED"] = "dice:stopped";
    ClientEvent["DICE_DRAG"] = "dice:drag";
    ClientEvent["DICE_SHAKE"] = "dice:shake";
    // Reconnection
    ClientEvent["STATE_REQUEST"] = "state:request";
    // Activity / presence
    ClientEvent["PLAYER_ACTIVE_STATUS"] = "player:active_status";
    ClientEvent["CLIENT_RESUME"] = "client:resume";
    // Voice chat (WebRTC signaling). Same family as DICE_DRAG: transient
    // real-time relays, deliberately NOT GameUpdate state — a dropped signal is
    // a renegotiation, not a desync. The server derives the voice room from the
    // socket's seated game room (client-supplied matchId is ignored), so only
    // co-located players can signal each other.
    ClientEvent["VOICE_JOIN"] = "voice:join";
    ClientEvent["VOICE_LEAVE"] = "voice:leave";
    ClientEvent["VOICE_OFFER"] = "voice:offer";
    ClientEvent["VOICE_ANSWER"] = "voice:answer";
    ClientEvent["VOICE_ICE"] = "voice:ice";
    ClientEvent["VOICE_END"] = "voice:end";
    // Social
    ClientEvent["CHAT_SEND"] = "chat:send";
    ClientEvent["GAME_CHAT_SEND"] = "game:chat_send";
    ClientEvent["FRIEND_ADD"] = "friend:add";
    ClientEvent["FRIEND_REMOVE"] = "friend:remove";
    ClientEvent["FRIEND_FAVOURITE"] = "friend:favourite";
    ClientEvent["FRIEND_BLOCK"] = "friend:block";
    ClientEvent["FRIEND_UNBLOCK"] = "friend:unblock";
    ClientEvent["FRIEND_CHALLENGE"] = "friend:challenge";
    ClientEvent["PLAYER_SEARCH"] = "player:search";
    // Lobby (public matchmaking surface — presence, chat, room list)
    ClientEvent["LOBBY_SUBSCRIBE"] = "lobby:subscribe";
    ClientEvent["LOBBY_UNSUBSCRIBE"] = "lobby:unsubscribe";
    ClientEvent["LOBBY_CHAT_SEND"] = "lobby:chat_send";
    ClientEvent["LOBBY_CREATE_ROOM"] = "lobby:create_room";
    ClientEvent["LOBBY_JOIN_ROOM"] = "lobby:join_room";
    // Crew
    ClientEvent["CREW_CREATE"] = "crew:create";
    ClientEvent["CREW_JOIN"] = "crew:join";
    ClientEvent["CREW_LEAVE"] = "crew:leave";
    ClientEvent["CREW_INVITE"] = "crew:invite";
    ClientEvent["CREW_KICK"] = "crew:kick";
    ClientEvent["CREW_DONATE"] = "crew:donate";
    ClientEvent["CREW_PROMOTE"] = "crew:promote";
    // Economy
    ClientEvent["GIFT_SEND"] = "gift:send";
    // Home Screen Data
    ClientEvent["HOME_DATA_REQUEST"] = "home:data_request";
    // Daily Bonus
    ClientEvent["DAILY_BONUS_CLAIM"] = "daily_bonus:claim";
    // Tutorial / onboarding. Client detects completion locally and reports it;
    // server grants idempotently. No exploit surface — Skip grants the same
    // reward with zero gameplay, so trusting the completion report is free.
    ClientEvent["TUTORIAL_MODULE_COMPLETE"] = "tutorial:module_complete";
    ClientEvent["TUTORIAL_SKIP"] = "tutorial:skip";
    // Connect-account 100M bonus — claimable once the account is linked to a real
    // Apple/Google identity (anti-abuse gate). Server grants idempotently.
    ClientEvent["ACCOUNT_CLAIM_CONNECT_BONUS"] = "account:claim_connect_bonus";
    // Notifications
    ClientEvent["FRIEND_REQUEST_ACCEPT"] = "friend:request_accept";
    ClientEvent["FRIEND_REQUEST_DECLINE"] = "friend:request_decline";
    ClientEvent["NOTIFICATIONS_SEEN"] = "notifications:seen";
    // Push notifications — client registers its FCM device token (one per
    // platform/install) so the server can reach it while the app is offline /
    // backgrounded. Unregister fires on logout so a shared device stops getting
    // the previous account's pushes.
    ClientEvent["PUSH_REGISTER_TOKEN"] = "push:register_token";
    ClientEvent["PUSH_UNREGISTER_TOKEN"] = "push:unregister_token";
    // Player
    ClientEvent["PLAYER_UPDATE_PROFILE"] = "player:update_profile";
    ClientEvent["PLAYER_EQUIP_DICE"] = "player:equip_dice";
    ClientEvent["PLAYER_SWITCH_PRESET"] = "player:switch_preset";
    // Offers (economy spec §8). Client re-requests its personalised set on
    // foreground/resume; claims free + video offers (paid offers go through the
    // normal /api/store/purchase IAP path with an offerId).
    ClientEvent["OFFERS_REQUEST"] = "offers:request";
    ClientEvent["OFFER_CLAIM"] = "offer:claim";
    // Utility
    ClientEvent["PING"] = "ping";
    ClientEvent["HEARTBEAT"] = "heartbeat";
})(ClientEvent || (ClientEvent = {}));
// ============================================================================
// SERVER → CLIENT EVENTS
// ============================================================================
export var ServerEvent;
(function (ServerEvent) {
    // Authentication
    ServerEvent["AUTH_SUCCESS"] = "auth:success";
    ServerEvent["AUTH_ERROR"] = "auth:error";
    ServerEvent["AUTH_SOCIAL_NEEDS_USERNAME"] = "auth:social_needs_username";
    ServerEvent["USERNAME_CHECK_RESULT"] = "username:check_result";
    // Matchmaking
    ServerEvent["MATCH_FOUND"] = "match:found";
    ServerEvent["MATCH_CANCELLED"] = "match:cancelled";
    ServerEvent["ROOM_CREATED"] = "room:created";
    ServerEvent["ROOM_JOINED"] = "room:joined";
    ServerEvent["ROOM_LEFT"] = "room:left";
    ServerEvent["ROOM_ERROR"] = "room:error";
    // Game State
    ServerEvent["GAME_STATE"] = "game:state";
    ServerEvent["GAME_STATE_DELTA"] = "game:delta";
    ServerEvent["GAME_STARTED"] = "game:started";
    ServerEvent["GAME_ENDED"] = "game:ended";
    // Game Events
    ServerEvent["GAME_PLAYER_JOINED"] = "game:player_joined";
    ServerEvent["GAME_PLAYER_LEFT"] = "game:player_left";
    ServerEvent["GAME_PLAYER_READY"] = "game:player_ready";
    ServerEvent["GAME_BET_PLACED"] = "game:bet_placed";
    /** @deprecated Replaced by `game:update.betZones` (broadcast) +
     *  `GAME_BET_ZONE_CREATE_ACK` (targeted to creator). Kept until the
     *  legacy CeeloRoom is migrated; no client listeners remain. */
    ServerEvent["GAME_BET_ZONE_CREATED"] = "game:bet_zone_created";
    /** @deprecated Replaced by `game:update.betZones`. */
    ServerEvent["GAME_BET_ZONE_UPDATED"] = "game:bet_zone_updated";
    /** @deprecated Replaced by `game:update.betZones`. */
    ServerEvent["GAME_BET_ZONE_CLOSED"] = "game:bet_zone_closed";
    /** Targeted ack to the zone creator only — carries the requestId
     *  so the local client can match its parked bills to the new zone
     *  and animate them in. Spectator visibility comes from the
     *  unified `game:update.betZones` broadcast. */
    ServerEvent["GAME_BET_ZONE_CREATE_ACK"] = "game:bet_zone_create_ack";
    ServerEvent["GAME_BET_ZONE_FADE_RESULT"] = "game:bet_zone_fade_result";
    ServerEvent["GAME_BET_ZONE_CREATE_REJECTED"] = "game:bet_zone_create_rejected";
    ServerEvent["GAME_TURN_START"] = "game:turn_start";
    ServerEvent["GAME_ROLL_AUTHORIZED"] = "game:roll_authorized";
    ServerEvent["GAME_ROLL_RESULT"] = "game:roll_result";
    ServerEvent["GAME_SIDE_BET_PLACED"] = "game:side_bet_placed";
    ServerEvent["GAME_SIDEBET_WINDOW"] = "game:sidebet_window";
    ServerEvent["GAME_ROLL_WINS"] = "game:roll_wins";
    ServerEvent["GAME_UPDATE"] = "game:update";
    ServerEvent["GAME_ROUND_END"] = "game:round_end";
    ServerEvent["GAME_PAYOUT"] = "game:payout";
    // Dice Sync (broadcast to all players)
    ServerEvent["GAME_DICE_SYNC"] = "game:dice_sync";
    // Server-Authoritative Dice Phase Events
    ServerEvent["STATE_FULL"] = "state:full";
    ServerEvent["DICE_AUTHORIZE"] = "dice:authorize";
    ServerEvent["DICE_PHASE"] = "dice:phase";
    ServerEvent["DICE_RESULT"] = "dice:result";
    ServerEvent["DICE_SHAKE"] = "dice:shake";
    // Reconnection
    ServerEvent["RECONNECT_TOKEN"] = "reconnect:token";
    ServerEvent["RECONNECT_OK"] = "reconnect:ok";
    ServerEvent["RECONNECT_FAIL"] = "reconnect:fail";
    // HI_LO specific
    ServerEvent["HI_LO_RESULTS"] = "hi_lo:results";
    // Voice chat (WebRTC signaling — see ClientEvent.VOICE_* notes)
    ServerEvent["VOICE_WELCOME"] = "voice:welcome";
    ServerEvent["VOICE_PEER_JOINED"] = "voice:peer-joined";
    ServerEvent["VOICE_PEER_LEFT"] = "voice:peer-left";
    ServerEvent["VOICE_OFFER"] = "voice:offer";
    ServerEvent["VOICE_ANSWER"] = "voice:answer";
    ServerEvent["VOICE_ICE"] = "voice:ice";
    ServerEvent["VOICE_END"] = "voice:end";
    // Social
    ServerEvent["CHAT_MESSAGE"] = "chat:message";
    ServerEvent["GAME_CHAT_MESSAGE"] = "game:chat_message";
    ServerEvent["FRIEND_REQUEST"] = "friend:request";
    ServerEvent["FRIEND_LIST"] = "friend:list";
    ServerEvent["FRIEND_ADDED"] = "friend:added";
    ServerEvent["FRIEND_REMOVED"] = "friend:removed";
    ServerEvent["FRIEND_FAVOURITED"] = "friend:favourited";
    ServerEvent["FRIEND_BLOCKED"] = "friend:blocked";
    ServerEvent["FRIEND_UNBLOCKED"] = "friend:unblocked";
    ServerEvent["BLOCKED_LIST"] = "blocked:list";
    ServerEvent["FRIEND_ONLINE"] = "friend:online";
    ServerEvent["FRIEND_OFFLINE"] = "friend:offline";
    ServerEvent["CHALLENGE_RECEIVED"] = "challenge:received";
    ServerEvent["PLAYER_SEARCH_RESULTS"] = "player:search_results";
    // Lobby
    ServerEvent["LOBBY_STATE"] = "lobby:state";
    ServerEvent["LOBBY_PLAYER_JOINED"] = "lobby:player_joined";
    ServerEvent["LOBBY_PLAYER_LEFT"] = "lobby:player_left";
    ServerEvent["LOBBY_PLAYER_UPDATED"] = "lobby:player_updated";
    ServerEvent["LOBBY_CHAT_MESSAGE"] = "lobby:chat_message";
    ServerEvent["LOBBY_ROOM_UPSERT"] = "lobby:room_upsert";
    ServerEvent["LOBBY_ROOM_REMOVED"] = "lobby:room_removed";
    // Crew
    ServerEvent["CREW_CREATED"] = "crew:created";
    ServerEvent["CREW_JOINED"] = "crew:joined";
    ServerEvent["CREW_LEFT"] = "crew:left";
    ServerEvent["CREW_UPDATED"] = "crew:updated";
    ServerEvent["CREW_MEMBER_JOINED"] = "crew:member_joined";
    ServerEvent["CREW_MEMBER_LEFT"] = "crew:member_left";
    // Economy
    ServerEvent["BALANCE_UPDATE"] = "balance:update";
    ServerEvent["GIFT_RECEIVED"] = "gift:received";
    ServerEvent["CONNECT_BONUS_RESULT"] = "account:connect_bonus_result";
    // Offers (economy spec §8)
    ServerEvent["OFFERS_ACTIVE"] = "offers:active";
    ServerEvent["OFFERS_UPDATE"] = "offers:update";
    ServerEvent["OFFER_CLAIM_RESULT"] = "offer:claim_result";
    // Home Screen Data
    ServerEvent["HOME_DATA"] = "home:data";
    // Daily Bonus
    ServerEvent["DAILY_BONUS_RESULT"] = "daily_bonus:result";
    // Dice Gear
    ServerEvent["DICE_GEAR_UPDATED"] = "player:gear_updated";
    // Notifications
    ServerEvent["NOTIFICATION"] = "notification";
    ServerEvent["NOTIFICATION_LIST"] = "notification:list";
    ServerEvent["NOTIFICATION_NEW"] = "notification:new";
    ServerEvent["NOTIFICATION_REMOVED"] = "notification:removed";
    ServerEvent["NOTIFICATION_UNSEEN_COUNT"] = "notification:unseen_count";
    // Utility
    ServerEvent["PONG"] = "pong";
    ServerEvent["ERROR"] = "error";
})(ServerEvent || (ServerEvent = {}));
//# sourceMappingURL=Events.js.map